import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem("perytus_token");

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }
    try {
      const [meResponse, statusResponse] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUser(meResponse.data);
      setSubscriptionStatus(statusResponse.data);
    } catch {
      localStorage.removeItem("perytus_token");
      setUser(null);
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("perytus_token", response.data.access_token);
    setUser(response.data.user);
    await refreshUser();
    return response.data;
  }, [refreshUser]);

  const register = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("perytus_token", response.data.access_token);
    setUser(response.data.user);
    await refreshUser();
    return response.data;
  }, [refreshUser]);

  const logout = useCallback(() => {
    localStorage.removeItem("perytus_token");
    setUser(null);
    setSubscriptionStatus(null);
  }, []);

  const canCreateProjects = useCallback(() => {
    return !!subscriptionStatus?.can_create_projects;
  }, [subscriptionStatus]);

  const value = useMemo(() => ({
    user,
    loading,
    subscriptionStatus,
    login,
    register,
    logout,
    getAuthHeaders,
    canCreateProjects,
    refreshUser
  }), [user, loading, subscriptionStatus, login, register, logout, getAuthHeaders, canCreateProjects, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
